import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Megaphone, ImagePlus, X } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { TextField } from "../shared/FormField";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";

const EMPTY_FORM = { title: "", body: "", date: "", image: "" };

export function ComposeBroadcastCard({ onSent }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [previewUrl, setPreviewUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef(null);

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPreviewUrl(URL.createObjectURL(file));
    setUploading(true);
    const body = new FormData();
    body.append("image", file);
    try {
      const { data } = await apiClient.post("/daily-menu/upload-image", body);
      setForm((f) => ({ ...f, image: data.path }));
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not upload the image."));
      setPreviewUrl("");
    } finally {
      setUploading(false);
    }
  };

  const clearImage = () => {
    setForm((f) => ({ ...f, image: "" }));
    setPreviewUrl("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await apiClient.post("/notifications/bulk", {
        title: form.title, body: form.body, date: form.date || null, image: form.image || null,
      });
      toast.success(
        data.dispatch.sent ? "Broadcast sent to all subscribers." : "Broadcast saved (push not delivered — stub/dev mode)."
      );
      setForm(EMPTY_FORM);
      setPreviewUrl("");
      onSent?.();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not send this broadcast."));
    } finally {
      setSending(false);
    }
  };

  return (
    <Card data-testid="compose-broadcast-card">
      <CardContent className="p-5">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
            <Megaphone size={18} strokeWidth={1.5} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-heading text-sm font-semibold text-text-main">Broadcast to Everyone</p>
            <p className="mt-0.5 text-xs text-text-muted">Reaches every customer subscribed to menu updates — e.g. today's special.</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <TextField label="Title" required value={form.title} onChange={setField("title")} testId="broadcast-form-title" />
          <div className="space-y-1.5">
            <Label htmlFor="broadcast-form-body">Message</Label>
            <Textarea id="broadcast-form-body" data-testid="broadcast-form-body" required rows={3} value={form.body} onChange={setField("body")} placeholder="e.g. Today's special: Paneer Butter Masala!" />
          </div>
          <TextField label="Date (optional)" type="date" value={form.date} onChange={setField("date")} testId="broadcast-form-date" />

          <div className="space-y-1.5">
            <Label>Photo (optional)</Label>
            {previewUrl ? (
              <div className="relative w-full max-w-[180px]">
                <img src={previewUrl} alt="Broadcast" data-testid="broadcast-form-image-preview" className="aspect-[4/3] w-full rounded-md border border-border object-cover" />
                <button type="button" onClick={clearImage} className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-danger text-white shadow" data-testid="broadcast-form-image-remove">
                  <X size={13} strokeWidth={2} />
                </button>
              </div>
            ) : (
              <label htmlFor="broadcast-form-image-input" className="flex h-24 w-full max-w-[180px] cursor-pointer flex-col items-center justify-center gap-1.5 rounded-md border border-dashed border-border bg-surface text-text-muted hover:bg-background">
                <ImagePlus size={18} strokeWidth={1.5} />
                <span className="text-xs">Upload photo</span>
              </label>
            )}
            <input ref={fileInputRef} id="broadcast-form-image-input" type="file" accept="image/*" className="hidden" onChange={handleFileChange} data-testid="broadcast-form-image-input" />
            {uploading && <p className="text-xs text-text-muted">Uploading...</p>}
          </div>

          <Button type="submit" variant="accent" className="w-full" disabled={sending || uploading} data-testid="broadcast-form-submit-button">
            {sending ? "Sending..." : "Send Broadcast"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
